// frontend/src/pages/Dashboard.jsx
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useFeatures } from '../context/FeaturesContext';
import { walletAPI, investmentAPI, notificationAPI, priceAPI } from '../services/api';
import { Card, Badge, Modal, Button, Spinner } from '../components/ui';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';
import { QRCodeSVG } from 'qrcode.react';
import {
  ArrowDownCircle, ArrowUpCircle, Send, QrCode, RefreshCw, Bell,
  ArrowLeftRight, TrendingUp, Copy, Info, ChevronRight, ArrowUpRight,
  Wifi, Users, Store, SendHorizonal, ShieldCheck,
} from 'lucide-react';
import { clsx } from 'clsx';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const CUSTOM_TOOLTIP = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#0D1C35', border: '1px solid #1E3454',
      borderRadius: 12, padding: '8px 12px', fontSize: 12,
    }}>
      <p style={{ color: '#8BA4C0', marginBottom: 2 }}>{label}</p>
      <p style={{ color: '#00A8DB', fontWeight: 700 }}>{payload[0]?.value?.toLocaleString()}</p>
    </div>
  );
};

const TX_SIGN = { deposit: '+', transfer_in: '+', investment_return: '+', commission: '+', savings_unlock: '+', refund: '+' };

const CURRENCY_META = {
  UGX:  { flag: '🇺🇬', color: '#00A8DB', symbol: 'USh' },
  USD:  { flag: '🇺🇸', color: '#10B981', symbol: '$'   },
  EUR:  { flag: '🇪🇺', color: '#8B5CF6', symbol: '€'   },
  USDT: { flag: '₮',   color: '#F59E0B', symbol: '₮'   },
};

const EUR_USD_APPROX = 1.09;

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

const QUICK_ACTIONS = [
  { to: '/wallet',          icon: ArrowDownCircle, label: 'Deposit',          color: '#00A8DB', bg: 'rgba(0,168,219,0.12)'   },
  { to: '/wallet',          icon: ArrowUpCircle,   label: 'Withdraw',         color: '#10B981', bg: 'rgba(16,185,129,0.12)'  },
  { to: '/exchange',        icon: ArrowLeftRight,  label: 'Buy USDT',         color: '#F59E0B', bg: 'rgba(245,158,11,0.12)'  },
  { to: '/exchange',        icon: Send,            label: 'Sell USDT',        color: '#EF4444', bg: 'rgba(239,68,68,0.12)'   },
  { to: '/investments',     icon: TrendingUp,      label: 'Investments',      color: '#8B5CF6', bg: 'rgba(139,92,246,0.12)'  },
  { to: '/merchant',        icon: Store,           label: 'Merchant Pay',     color: '#14B8A6', bg: 'rgba(20,184,166,0.12)'  },
  { to: '/internet',             icon: Wifi,            label: 'Internet',    color: '#1B61AC', bg: 'rgba(27,97,172,0.12)'   },
  { to: '/referral',        icon: Users,           label: 'Referrals',        color: '#EC4899', bg: 'rgba(236,72,153,0.12)'  },
];

function PortfolioRow({ label, native, usd }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 0', borderBottom: '1px solid rgba(30,52,84,0.5)',
    }}>
      <p style={{ fontSize: 13, color: '#8BA4C0', margin: 0 }}>{label}</p>
      <div style={{ textAlign: 'right' }}>
        <p style={{ fontSize: 14, fontWeight: 700, color: '#F0F6FF', margin: 0 }}>
          ${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
        <p style={{ fontSize: 10, color: '#4E677E', margin: 0 }}>{native}</p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { features } = useFeatures();
  const investEnabled = !(features && features.investment_projects && features.investment_projects.isEnabled === false);

  const [wallet, setWallet]               = useState(null);
  const [transactions, setTransactions]   = useState([]);
  const [investmentSummary, setInvSummary]= useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [qrOpen, setQrOpen]               = useState(false);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [lastValuedAt, setLastValuedAt]   = useState(null);
  const [priceData, setPriceData]         = useState(null);
  const [ugxPerUsd, setUgxPerUsd]         = useState(3750);
  const [walletSpark, setWalletSpark]     = useState([900, 950, 980, 1050, 1100, 1200, 1284]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [wRes, invRes, notifRes, priceRes] = await Promise.allSettled([
        walletAPI.getBalance(),
        investEnabled ? investmentAPI.getMyInvestments() : Promise.resolve({ summary: { totalInvested:0, totalReturns:0, activeCount:0 } }),
        notificationAPI.getAll({ limit: 5 }),
        priceAPI.getUsdt(),
      ]);
      if (wRes.status === 'fulfilled')    setWallet(wRes.value.wallet);
      if (invRes.status === 'fulfilled')  setInvSummary(invRes.value.summary);
      if (notifRes.status === 'fulfilled') {
        const notifs = notifRes.value.notifications || [];
        setNotifications(notifs.slice(0, 4));
        setUnreadCount(notifs.filter(n => !n.read).length);
      }
      if (priceRes.status === 'fulfilled') {
        const p = priceRes.value.price;
        setPriceData(p);
        const rate = p?.marketUgxPerUsdt || p?.buyUgxPerUsdt || 3750;
        setUgxPerUsd(rate);
        setLastValuedAt(new Date());
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      const res = await walletAPI.getTransactions({ limit: 6, page: 1 });
      setTransactions(res.transactions || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchAll(); fetchTransactions(); }, [fetchAll, fetchTransactions]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') { fetchAll(); fetchTransactions(); }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchAll, fetchTransactions]);

  useEffect(() => {
    const id = setInterval(() => { fetchAll(); fetchTransactions(); }, 60000);
    return () => clearInterval(id);
  }, [fetchAll, fetchTransactions]);

  useEffect(() => {
    const onTx = () => { fetchAll(); fetchTransactions(); };
    window.addEventListener('dwi:transaction', onTx);
    return () => window.removeEventListener('dwi:transaction', onTx);
  }, [fetchAll, fetchTransactions]);

  const w   = wallet || { balance: 0, savingsBalance: 0, commissionBalance: 0, investmentBalance: 0, ugxBalance: 0, usdtBalance: 0, eurBalance: 0 };
  const inv = investmentSummary || { totalInvested: 0, totalReturns: 0, activeCount: 0 };

  const ugxInUsd  = (w.ugxBalance  || 0) / ugxPerUsd;
  const usdtInUsd = (w.usdtBalance || 0);
  const eurInUsd  = (w.eurBalance  || 0) * EUR_USD_APPROX;

  const totalBalance =
    (w.balance || 0) + (w.savingsBalance || 0) + (w.commissionBalance || 0) +
    (w.investmentBalance || 0) + ugxInUsd + usdtInUsd + eurInUsd;

  const breakdownRows = [
    { label: 'UGX Wallet',     native: `USh ${(w.ugxBalance||0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`,  usd: ugxInUsd },
    { label: 'USD Wallet',     native: `$${(w.balance||0).toFixed(2)}`,        usd: w.balance || 0 },
    { label: 'USDT Wallet',    native: `₮ ${(w.usdtBalance||0).toFixed(6)}`,   usd: usdtInUsd },
    { label: 'EUR Wallet',     native: `€ ${(w.eurBalance||0).toFixed(2)}`,     usd: eurInUsd },
    { label: 'Savings',        native: `$${(w.savingsBalance||0).toFixed(2)}`,  usd: w.savingsBalance || 0 },
    ...(investEnabled ? [{ label: 'Investments', native: `$${(w.investmentBalance||0).toFixed(2)}`, usd: w.investmentBalance || 0 }] : []),
    { label: 'Commission',     native: `$${(w.commissionBalance||0).toFixed(2)}`,usd: w.commissionBalance || 0 },
    { label: 'Returns Earned', native: `$${(inv.totalReturns||0).toFixed(2)}`,  usd: inv.totalReturns || 0 },
  ];

  const currencies = [
    { code: 'UGX',  balance: w.ugxBalance  || 0 },
    { code: 'USD',  balance: w.balance      || 0 },
    { code: 'EUR',  balance: w.eurBalance   || 0 },
    { code: 'USDT', balance: w.usdtBalance  || 0 },
  ].filter(c => c.balance > 0 || c.code === 'UGX' || c.code === 'USD');

  const qrData = JSON.stringify({ type: 'dwi_payment', userId: user?._id, name: user?.name, email: user?.email });
  const copyWalletId = () => navigator.clipboard.writeText(user?._id || '').then(() => toast.success('Wallet ID copied!'));
  const priceSourceLabel = priceData ? `DWI Pricing Engine (${priceData.priceSource || 'fallback'})` : 'DWI Pricing Engine';

  return (
    <div className="space-y-5 animate-slide-up max-w-7xl mx-auto">

      {/* ── P2P Rates Admin Banner (admin only) ── */}
      {(user?.role === 'admin' || user?.role === 'super_admin') && priceData?.p2pRatesStale && priceData?.pricingMode === 'manual' && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.40)',
          borderRadius: 12, padding: '12px 16px',
        }}>
          <span style={{ color: '#F59E0B', fontSize: 16, marginTop: 1 }}>⚠</span>
          <div>
            <p style={{ color: '#FCD34D', fontWeight: 700, fontSize: 13, margin: 0 }}>
              P2P rates are {priceData.p2pRatesAgeHours != null ? `${priceData.p2pRatesAgeHours}h` : 'very'} old — exchange is quoting these rates
            </p>
            <p style={{ color: '#92400E', fontSize: 12, margin: '3px 0 0' }}>
              Update now in <a href="/treasury" style={{ color: '#F59E0B', fontWeight: 600 }}>Treasury → P2P Rates</a> to keep prices accurate.
            </p>
          </div>
        </div>
      )}
      {(user?.role === 'admin' || user?.role === 'super_admin') && priceData?.p2pFallbackActive && priceData?.pricingMode === 'manual_with_fallback' && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.40)',
          borderRadius: 12, padding: '12px 16px',
        }}>
          <span style={{ color: '#EF4444', fontSize: 16, marginTop: 1 }}>⚠</span>
          <div>
            <p style={{ color: '#FCA5A5', fontWeight: 700, fontSize: 13, margin: 0 }}>
              Exchange running on CoinGecko fallback
            </p>
            <p style={{ color: '#7F1D1D', fontSize: 12, margin: '3px 0 0' }}>
              {priceData.p2pFallbackReason} — Go to <a href="/treasury" style={{ color: '#EF4444', fontWeight: 600 }}>Treasury → P2P Rates</a> to update.
            </p>
          </div>
        </div>
      )}

      {/* ── Greeting Row ── */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <p style={{ fontSize: 13, color: '#8BA4C0', margin: 0 }}>{getGreeting()},</p>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#F0F6FF', margin: '2px 0 0', letterSpacing: '-0.02em' }}>
            {user?.name?.split(' ')[0]} 👋
          </h1>
          {user?.kyc?.status !== 'approved' && (
            <Link to="/kyc" style={{ fontSize: 12, color: '#F59E0B', display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, textDecoration: 'none' }}>
              <ShieldCheck size={13} /> Complete KYC to unlock all features
            </Link>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={() => { fetchAll(); fetchTransactions(); }}
            style={{
              background: 'rgba(0,168,219,0.08)', border: '1px solid rgba(0,168,219,0.15)',
              borderRadius: 10, padding: '8px', color: '#8BA4C0', cursor: 'pointer',
              display: 'flex', alignItems: 'center', transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color='#00A8DB'; e.currentTarget.style.borderColor='rgba(0,168,219,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.color='#8BA4C0'; e.currentTarget.style.borderColor='rgba(0,168,219,0.15)'; }}
            title="Refresh"
          >
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {/* ── Hero Balance Card ── */}
      <div style={{
        borderRadius: 20, overflow: 'hidden', position: 'relative',
        background: 'linear-gradient(135deg, #0D2B5E 0%, #0D1C35 60%, #0A1628 100%)',
        border: '1px solid rgba(0,168,219,0.18)',
        boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
      }}>
        {/* Glow overlay */}
        <div style={{
          position: 'absolute', top: '-60px', right: '-40px',
          width: 300, height: 300, borderRadius: '50%', pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(0,168,219,0.15) 0%, transparent 70%)',
        }} />
        <div style={{
          position: 'absolute', bottom: '-40px', left: '-20px',
          width: 200, height: 200, borderRadius: '50%', pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(27,97,172,0.12) 0%, transparent 70%)',
        }} />

        <div style={{ position: 'relative', padding: '24px 24px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'rgba(240,246,255,0.45)', letterSpacing: '0.12em', textTransform: 'uppercase', margin: 0 }}>
              Total Portfolio Value
            </p>
            <button
              onClick={() => setPortfolioOpen(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'rgba(0,168,219,0.7)', fontSize: 11, background: 'none', border: 'none', cursor: 'pointer', transition: 'color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.color = '#00A8DB'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(0,168,219,0.7)'}
            >
              <Info size={13} /> Details
            </button>
          </div>

          {loading ? (
            <div className="skeleton" style={{ height: 44, width: 200, marginBottom: 8 }} />
          ) : (
            <p
              onClick={() => setPortfolioOpen(true)}
              style={{ fontSize: 38, fontWeight: 900, color: '#F0F6FF', letterSpacing: '-0.04em', margin: '4px 0 4px', cursor: 'pointer', lineHeight: 1 }}
            >
              ${totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          )}
          <p style={{ fontSize: 12, color: 'rgba(240,246,255,0.4)', margin: '0 0 20px' }}>
            All wallets & investments · <span style={{ color: '#00A8DB' }}>{ugxPerUsd.toLocaleString()} UGX/$</span>
          </p>

          {/* Action row */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { to: '/wallet',   icon: ArrowDownCircle, label: 'Deposit',  primary: true },
              { to: '/wallet',   icon: ArrowUpCircle,   label: 'Withdraw', primary: false },
              { to: '/wallet',   icon: Send,            label: 'Send',     primary: false },
              { to: '/exchange', icon: ArrowLeftRight,  label: 'Exchange', primary: false },
              { qr: true,        icon: QrCode,          label: 'QR Pay',   primary: false },
            ].map(({ to, icon: Icon, label, primary, qr }) => (
              <button
                key={label}
                onClick={qr ? () => setQrOpen(true) : () => navigate(to)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, fontFamily: 'Inter, sans-serif',
                  transition: 'all 0.15s',
                  background: primary
                    ? 'linear-gradient(135deg,#1B61AC,#00A8DB)'
                    : 'rgba(255,255,255,0.08)',
                  color: primary ? 'white' : 'rgba(240,246,255,0.85)',
                  border: primary ? 'none' : '1px solid rgba(255,255,255,0.10)',
                  boxShadow: primary ? '0 4px 16px rgba(0,168,219,0.3)' : 'none',
                }}
              >
                <Icon size={14} />{label}
              </button>
            ))}
          </div>
        </div>

        {/* Currency strip */}
        {!loading && (
          <div style={{
            display: 'flex', padding: '10px 24px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            background: 'rgba(0,0,0,0.15)',
            gap: 24, overflowX: 'auto',
          }}>
            {currencies.map(({ code, balance }) => {
              const meta = CURRENCY_META[code];
              return (
                <div key={code} style={{ flexShrink: 0 }}>
                  <p style={{ fontSize: 9, fontWeight: 700, color: 'rgba(240,246,255,0.35)', letterSpacing: '0.10em', textTransform: 'uppercase', margin: 0 }}>
                    {meta.flag} {code}
                  </p>
                  <p style={{ fontSize: 14, fontWeight: 700, color: meta.color, margin: '2px 0 0', fontVariantNumeric: 'tabular-nums' }}>
                    {meta.symbol}{balance.toLocaleString('en-US', { maximumFractionDigits: code === 'UGX' ? 0 : 2 })}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Quick Actions ── */}
      <div>
        <p style={{ fontSize: 11, fontWeight: 700, color: '#4E677E', letterSpacing: '0.10em', textTransform: 'uppercase', margin: '0 0 12px' }}>
          Quick Actions
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }} className="sm:grid-cols-4 grid-cols-4">
          {QUICK_ACTIONS.filter(a => a.to !== '/investments' || investEnabled).map(({ to, icon: Icon, label, color, bg }) => (
            <Link
              key={label}
              to={to}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 8, padding: '16px 8px',
                background: '#0D1C35', border: '1px solid #1E3454',
                borderRadius: 16, textDecoration: 'none',
                transition: 'all 0.2s ease',
              }}
              className="card-hover ripple"
              onMouseEnter={e => { e.currentTarget.style.borderColor = color + '50'; e.currentTarget.style.background = bg; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#1E3454'; e.currentTarget.style.background = '#0D1C35'; }}
            >
              <div style={{
                width: 40, height: 40, borderRadius: 12,
                background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `1px solid ${color}25`,
              }}>
                <Icon size={18} color={color} />
              </div>
              <span style={{ fontSize: 10, fontWeight: 600, color: '#8BA4C0', textAlign: 'center', lineHeight: 1.3 }}>
                {label}
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* ── Chart + Notifications ── */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Activity Chart */}
        <div style={{
          background: '#0D1C35', border: '1px solid #1E3454', borderRadius: 16, padding: 20,
        }} className="lg:col-span-2">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#F0F6FF', margin: 0 }}>Wallet Activity</p>
            <Link to="/wallet" style={{ fontSize: 11, color: '#00A8DB', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
              All Transactions <ArrowUpRight size={12} />
            </Link>
          </div>
          <ResponsiveContainer width="100%" height={130}>
            <AreaChart data={walletSpark.map((v, i) => ({ day: `Day ${i + 1}`, value: v }))}>
              <defs>
                <linearGradient id="wGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00A8DB" stopOpacity={0.25}/>
                  <stop offset="100%" stopColor="#00A8DB" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="day" tick={false} axisLine={false} tickLine={false}/>
              <YAxis hide/>
              <Tooltip content={<CUSTOM_TOOLTIP/>}/>
              <Area type="monotone" dataKey="value" stroke="#00A8DB" fill="url(#wGrad)" strokeWidth={2} dot={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Notifications */}
        <div style={{ background: '#0D1C35', border: '1px solid #1E3454', borderRadius: 16, padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#F0F6FF', margin: 0 }}>Notifications</p>
            <Link to="/notifications" style={{ fontSize: 11, color: '#00A8DB', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
              View All <ArrowUpRight size={12} />
            </Link>
          </div>
          {notifications.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Bell size={28} color="#4E677E" style={{ marginBottom: 8 }} />
              <p style={{ fontSize: 12, color: '#4E677E', margin: 0 }}>No recent notifications</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {notifications.map(n => (
                <div key={n._id} style={{
                  display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 12,
                  background: n.read ? 'transparent' : 'rgba(0,168,219,0.06)',
                  border: n.read ? '1px solid #1E3454' : '1px solid rgba(0,168,219,0.20)',
                }}>
                  {!n.read && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00A8DB', marginTop: 5, flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: '#F0F6FF', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {n.title || 'Notification'}
                    </p>
                    <p style={{ fontSize: 10, color: '#4E677E', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {n.message || n.body || '—'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Recent Transactions ── */}
      <div style={{ background: '#0D1C35', border: '1px solid #1E3454', borderRadius: 16, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: '#F0F6FF', margin: 0 }}>Recent Transactions</p>
          <Link to="/wallet" style={{ fontSize: 11, color: '#00A8DB', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
            All <ArrowUpRight size={12} />
          </Link>
        </div>

        {transactions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <p style={{ fontSize: 13, color: '#4E677E', margin: '0 0 12px' }}>No transactions yet — make your first deposit</p>
            <Link to="/wallet" style={{
              display: 'inline-block', padding: '8px 20px', borderRadius: 10,
              background: 'linear-gradient(135deg,#1B61AC,#00A8DB)',
              color: 'white', fontSize: 13, fontWeight: 600, textDecoration: 'none',
            }}>Deposit Now</Link>
          </div>
        ) : (
          <div>
            {transactions.map((tx, idx) => {
              const sign = TX_SIGN[tx.type] || '-';
              const isCredit = sign === '+';
              return (
                <div key={tx._id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 0', gap: 12,
                  borderBottom: idx < transactions.length - 1 ? '1px solid rgba(30,52,84,0.5)' : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 12, flexShrink: 0,
                      background: isCredit ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 16,
                    }}>
                      {isCredit ? <ArrowDownCircle size={16} color="#10B981" /> : <ArrowUpCircle size={16} color="#EF4444" />}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#F0F6FF', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                        {tx.type?.replace(/_/g, ' ')}
                      </p>
                      <p style={{ fontSize: 10, color: '#4E677E', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {tx.paymentMethod?.replace(/_/g, ' ')} · {tx.createdAt ? format(new Date(tx.createdAt), 'dd MMM HH:mm') : '—'}
                      </p>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <p style={{ fontSize: 14, fontWeight: 700, color: isCredit ? '#10B981' : '#EF4444', margin: 0 }}>
                      {sign}${tx.amount?.toFixed(2)}
                    </p>
                    <span style={{
                      fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                      padding: '2px 6px', borderRadius: 4,
                      background: tx.status === 'completed' ? 'rgba(16,185,129,0.12)' : tx.status === 'pending' ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)',
                      color: tx.status === 'completed' ? '#10B981' : tx.status === 'pending' ? '#F59E0B' : '#EF4444',
                    }}>
                      {tx.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Investment Snapshot ── */}
      {investEnabled && inv.activeCount > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(27,97,172,0.08))',
          border: '1px solid rgba(139,92,246,0.20)',
          borderRadius: 16, padding: '16px 20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'rgba(139,92,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <TrendingUp size={20} color="#8B5CF6" />
            </div>
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#F0F6FF', margin: 0 }}>Active Investments</p>
              <p style={{ fontSize: 12, color: '#8BA4C0', margin: '2px 0 0' }}>
                {inv.activeCount} fund{inv.activeCount !== 1 ? 's' : ''} · ${(inv.totalReturns || 0).toFixed(2)} earned
              </p>
            </div>
          </div>
          <Link to="/investments" style={{
            fontSize: 12, fontWeight: 600, color: '#8B5CF6',
            display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none',
          }}>
            View <ChevronRight size={14} />
          </Link>
        </div>
      )}

      {/* ── QR Payment Modal ── */}
      <Modal open={qrOpen} onClose={() => setQrOpen(false)} title="Your Payment QR Code" size="sm">
        <div className="flex flex-col items-center gap-5">
          <p className="text-text-muted text-sm text-center">
            Share this QR code so others can send money to your DWI wallet instantly.
          </p>
          <div className="bg-white p-4 rounded-2xl shadow-card">
            <QRCodeSVG value={qrData} size={200} bgColor="#FFFFFF" fgColor="#0A1628" level="M" includeMargin={false}/>
          </div>
          <div className="w-full space-y-2">
            <p className="text-[10px] font-bold text-text-muted uppercase tracking-widest text-center">Wallet ID</p>
            <div className="flex items-center gap-2 bg-bg-panel border border-border rounded-xl px-3 py-2.5">
              <code className="text-cyan text-xs font-mono flex-1 break-all">{user?._id}</code>
              <button onClick={copyWalletId} className="text-text-muted hover:text-cyan flex-shrink-0 transition-colors">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={() => setQrOpen(false)}>Close</Button>
        </div>
      </Modal>

      {/* ── Portfolio Details Modal ── */}
      <Modal open={portfolioOpen} onClose={() => setPortfolioOpen(false)} title="Portfolio Details" size="md">
        <div className="space-y-4">
          <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(0,168,219,0.08)', borderRadius: 12, border: '1px solid rgba(0,168,219,0.20)' }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#8BA4C0', letterSpacing: '0.10em', textTransform: 'uppercase', margin: '0 0 4px' }}>Net Worth (USD Equivalent)</p>
            <p style={{ fontSize: 30, fontWeight: 900, color: '#00A8DB', margin: 0 }}>
              ${totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>

          <div style={{ background: '#0D1C35', border: '1px solid #1E3454', borderRadius: 12 }}>
            {breakdownRows.map(row => (
              <div key={row.label} style={{ padding: '0 16px' }}>
                <PortfolioRow label={row.label} native={row.native} usd={row.usd} />
              </div>
            ))}
          </div>

          <div style={{ background: 'rgba(0,168,219,0.06)', border: '1px solid rgba(0,168,219,0.15)', borderRadius: 12, padding: 16 }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: '#00A8DB', letterSpacing: '0.10em', textTransform: 'uppercase', margin: '0 0 10px' }}>Rate Used for Valuation</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: 12 }}>
              <div>
                <p style={{ color: '#8BA4C0', margin: 0 }}>Market Rate</p>
                <p style={{ color: '#F0F6FF', fontWeight: 700, margin: '2px 0 0' }}>1 USD = {(priceData?.marketUgxPerUsdt || ugxPerUsd).toLocaleString()} UGX</p>
              </div>
              <div>
                <p style={{ color: '#8BA4C0', margin: 0 }}>Buy Rate</p>
                <p style={{ color: '#10B981', fontWeight: 700, margin: '2px 0 0' }}>{priceData?.buyUgxPerUsdt ? `${priceData.buyUgxPerUsdt.toLocaleString()} UGX/USDT` : '—'}</p>
              </div>
              <div>
                <p style={{ color: '#8BA4C0', margin: 0 }}>Source</p>
                <p style={{ color: '#F0F6FF', margin: '2px 0 0' }}>{priceSourceLabel}</p>
              </div>
              <div>
                <p style={{ color: '#8BA4C0', margin: 0 }}>Valued At</p>
                <p style={{ color: '#F0F6FF', margin: '2px 0 0' }}>{lastValuedAt ? format(lastValuedAt, 'dd MMM HH:mm:ss') : 'Loading...'}</p>
              </div>
            </div>
          </div>

          <Button variant="outline" className="w-full" onClick={() => setPortfolioOpen(false)}>Close</Button>
        </div>
      </Modal>
    </div>
  );
}
